// Region picker dropdown for the PartyPanel. Click the row to expand a
// popover listing every region with a PingBadge next to each (sorted by
// measured ping ascending). Click an entry to select; click outside or
// press Escape to close. Locked state hides the chevron + suppresses
// click handling for non-owner members.
//
// Live ping data: subscribes to regionPing on mount, updates badges +
// re-sorts the popover list whenever new measurements arrive.

import { REGIONS, getRegionById, type Region } from '../../net/regions';
import { subscribe as subscribePings, type RegionPingHints } from '../../net/regionPing';
import { makePingBadge, type PingBadgeHandle } from '../components/PingBadge';
import { menuAudio } from '../MenuAudio';
import { MenuNav, type MenuItem } from '../MenuNav';
import { t } from '../../i18n';

const STYLES_ID = 'region-picker-styles';
const STYLES = `
.region-picker {
  background: #FFFFFF;
  border-radius: 18px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.18), inset 0 -3px 0 rgba(176, 209, 219, 0.6);
  padding: 14px 18px;
  display: flex; align-items: center; justify-content: space-between;
  gap: 14px;
  position: relative;
  cursor: pointer;
  font-family: 'Chewy', sans-serif;
  user-select: none;
}
/* When the picker popover is open, raise the whole row above its
   siblings inside the PartyPanel body. Without this, the next field
   row (Your Join Link) renders ON TOP of the popover even though the
   popover's z-index is set, because no positioned ancestor created a
   stacking context yet. */
.region-picker.open { z-index: 50; }
.region-picker.locked { cursor: not-allowed; }
.region-picker:hover:not(.locked),
.region-picker.focused {
  filter: brightness(1.04);
  outline: 4px solid rgba(255,255,255,0.7);
  outline-offset: 3px;
}
.region-picker .stack {
  display: flex; flex-direction: column;
  flex: 1; min-width: 0;
}
.region-picker .label {
  font-size: 18px;
  color: #6CABC1;
  letter-spacing: 0.04em;
  margin-bottom: 2px;
}
.region-picker .value-row {
  display: flex; align-items: center; gap: 12px;
}
.region-picker .value {
  font-size: 32px;
  color: #0E3C4A;
  letter-spacing: 0.04em;
  line-height: 1;
}
.region-picker .chevron {
  width: 28px; height: 28px;
  background: linear-gradient(180deg, #F1F9FB 0%, #C3D6DB 100%);
  border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  font-size: 18px;
  color: #0E3C4A;
  transition: transform 160ms ease;
}
.region-picker.open .chevron { transform: rotate(180deg); }
.region-picker.locked .chevron { display: none; }
.region-picker .lock-pill {
  background: rgba(108, 171, 193, 0.18);
  color: #6CABC1;
  font-size: 14px;
  padding: 4px 10px;
  border-radius: 999px;
  letter-spacing: 0.04em;
  display: none;
}
.region-picker.locked .lock-pill { display: inline-flex; }

.region-picker-popover {
  position: absolute;
  left: 0; right: 0; top: calc(100% + 6px);
  background: #FFFFFF;
  border-radius: 14px;
  box-shadow: 0 10px 24px rgba(0,0,0,0.35);
  z-index: 5;
  overflow: hidden;
  display: none;
}
.region-picker.open .region-picker-popover { display: block; }
.region-picker-popover .opt {
  display: flex; align-items: center; justify-content: space-between;
  gap: 14px;
  padding: 12px 18px;
  cursor: pointer;
  font-size: 22px;
  color: #0E3C4A;
  border-bottom: 1px solid rgba(176, 209, 219, 0.4);
  transition: background-color 120ms ease;
}
.region-picker-popover .opt:last-child { border-bottom: 0; }
.region-picker-popover .opt:hover,
.region-picker-popover .opt.focused {
  background: rgba(143, 211, 233, 0.22);
}
.region-picker-popover .opt.selected {
  background: rgba(143, 211, 233, 0.35);
}
.region-picker-popover .opt .name {
  flex: 1;
  white-space: nowrap;
  overflow: hidden; text-overflow: ellipsis;
}
.region-picker-popover .opt .check {
  font-size: 20px;
  color: #6FCEEB;
  visibility: hidden;
}
.region-picker-popover .opt.selected .check { visibility: visible; }
`;

export interface RegionPickerCallbacks {
  /** Fired when the user picks a different region. The picker doesn't
   *  update its own selected state — caller is responsible for re-calling
   *  setSelected() once the migration / preference write completes. */
  onChange(regionId: string): void;
  /** Fired when the popover opens/closes. Caller (PartyPanel) uses this
   *  to pause its own MenuNav so arrow keys + gamepad don't fight the
   *  picker's internal nav. */
  onOpenStateChange?(open: boolean): void;
}

export interface RegionPickerHandle {
  el: HTMLElement;
  setSelected(regionId: string): void;
  setLocked(locked: boolean): void;
  /** Imperative show / hide of the popover. */
  setOpen(open: boolean): void;
  dispose(): void;
}

export function makeRegionPicker(callbacks: RegionPickerCallbacks): RegionPickerHandle {
  ensureStyles();

  const root = document.createElement('div');
  root.className = 'region-picker mode-solo';
  root.innerHTML = `
    <div class="stack">
      <div class="label">${t('region.label')}</div>
      <div class="value-row">
        <div class="value">—</div>
        <span class="lock-pill">${t('region.locked')}</span>
      </div>
    </div>
    <div class="chevron">▾</div>
    <div class="region-picker-popover"></div>
  `;
  const valueEl = root.querySelector('.value') as HTMLElement;
  const popover = root.querySelector('.region-picker-popover') as HTMLElement;

  let selectedId: string = REGIONS[0]!.id;
  let locked = false;
  let open = false;
  let pings: RegionPingHints = {};
  const optBadges = new Map<string, PingBadgeHandle>();
  /** Internal MenuNav for the popover options. Built when opening,
   *  disposed when closing — driving arrow keys / gamepad through the
   *  region list. */
  let popoverNav: MenuNav | null = null;

  const renderOptions = (): void => {
    // Sort by measured ping ascending; regions without a measurement go
    // last (Number.MAX_SAFE_INTEGER as the sort key).
    const sorted = REGIONS
      .slice()
      .sort((a, b) => {
        const pa = pings[a.id] ?? Number.MAX_SAFE_INTEGER;
        const pb = pings[b.id] ?? Number.MAX_SAFE_INTEGER;
        return pa - pb;
      });
    // Re-render. Cheap — at most 5 rows.
    for (const b of optBadges.values()) b.dispose();
    optBadges.clear();
    popover.innerHTML = '';
    for (const r of sorted) {
      const opt = document.createElement('div');
      opt.className = 'opt' + (r.id === selectedId ? ' selected' : '');
      opt.setAttribute('data-region', r.id);
      opt.innerHTML = `
        <span class="check">✓</span>
        <span class="name">${escapeHtml(r.label)}</span>
        <span class="badge-host"></span>
      `;
      const host = opt.querySelector('.badge-host') as HTMLElement;
      const badge = makePingBadge({ size: 'sm' });
      badge.setPing(pings[r.id] ?? 0);
      host.appendChild(badge.el);
      optBadges.set(r.id, badge);
      opt.addEventListener('click', (e) => {
        e.stopPropagation();
        if (locked) return;
        if (r.id !== selectedId) {
          menuAudio.playClick();
          callbacks.onChange(r.id);
        }
        setOpen(false);
      });
      popover.appendChild(opt);
    }
  };

  const renderSelectedLabel = (): void => {
    const r = getRegionById(selectedId) ?? REGIONS[0]!;
    valueEl.textContent = r.label;
  };

  const buildPopoverNav = (): void => {
    popoverNav?.dispose();
    const items: MenuItem[] = [];
    const opts = popover.querySelectorAll('.opt') as NodeListOf<HTMLElement>;
    let initialIndex = 0;
    opts.forEach((opt, i) => {
      const regionId = opt.getAttribute('data-region') ?? '';
      if (regionId === selectedId) initialIndex = i;
      items.push({
        el: opt,
        row: i,
        col: 0,
        onActivate: () => {
          if (locked) return;
          if (regionId !== selectedId) {
            menuAudio.playClick();
            callbacks.onChange(regionId);
          }
          setOpen(false);
        }
      });
    });
    if (items.length === 0) return;
    popoverNav = new MenuNav({
      items,
      initialIndex,
      onCancel: () => setOpen(false)
    });
    popoverNav.mount();
  };

  const setOpen = (next: boolean): void => {
    if (locked && next) return;
    if (open === next) return;
    open = next;
    root.classList.toggle('open', open);
    if (open) {
      menuAudio.playClick();
      // Refresh sort + selected highlight on each open so it reflects
      // the current ping snapshot.
      renderOptions();
      // Build the popover's internal nav AFTER renderOptions so the
      // newly-rendered .opt elements exist for MenuItem lookup.
      buildPopoverNav();
      // Outside-click closes the popover. Defer registration by a tick so
      // the click that opened the picker doesn't immediately close it.
      setTimeout(() => addEventListener('click', onOutsideClick, { capture: true }), 0);
    } else {
      popoverNav?.dispose();
      popoverNav = null;
      removeEventListener('click', onOutsideClick, { capture: true } as EventListenerOptions);
    }
    callbacks.onOpenStateChange?.(open);
  };

  const onOutsideClick = (e: MouseEvent): void => {
    if (!open) return;
    if (root.contains(e.target as Node)) return;
    setOpen(false);
  };

  root.addEventListener('click', () => {
    if (locked) return;
    setOpen(!open);
  });

  // Subscribe to live ping updates. Returns an unsubscribe we keep for
  // dispose().
  const detachPings = subscribePings((map) => {
    pings = map;
    // Update each open badge in place if the popover is showing; cheap.
    for (const [id, badge] of optBadges) {
      badge.setPing(map[id] ?? 0);
    }
  });

  renderSelectedLabel();
  renderOptions();

  return {
    el: root,
    setSelected(id: string): void {
      if (id === selectedId) return;
      selectedId = id;
      renderSelectedLabel();
      // Mirror the .selected class on whatever option matches.
      const opts = popover.querySelectorAll('.opt') as NodeListOf<HTMLElement>;
      for (const o of opts) {
        o.classList.toggle('selected', o.getAttribute('data-region') === id);
      }
    },
    setLocked(next: boolean): void {
      if (locked === next) return;
      locked = next;
      root.classList.toggle('locked', locked);
      if (locked) setOpen(false);
    },
    setOpen,
    dispose(): void {
      detachPings();
      popoverNav?.dispose();
      popoverNav = null;
      removeEventListener('click', onOutsideClick, { capture: true } as EventListenerOptions);
      for (const b of optBadges.values()) b.dispose();
      optBadges.clear();
      root.remove();
    }
  };
}

function ensureStyles(): void {
  if (document.getElementById(STYLES_ID)) return;
  const s = document.createElement('style');
  s.id = STYLES_ID;
  s.textContent = STYLES;
  document.head.appendChild(s);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

/** Small utility re-export so PartyPanel can pass a Region label for the
 *  switch-modal title without re-importing regions.ts directly. */
export { type Region };
